import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './Nav.css';

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [nav, setNav] = useState({ recipes: [], ingredients: [] });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    api.get('/nav').then(res => setNav(res.data.nav)).catch(() => {});
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const openDropdown = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(name);
  };

  const closeDropdown = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 250);
  };

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { state: { message: 'You have been signed out.' } });
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    : '?';

  return (
    <nav className="nav">
      <div className="nav__inner container">

        {/* Logo */}
        <Link to="/" className="nav__logo">
          <span className="nav__logo-icon">🍴</span>
          <span className="nav__logo-text">Recipe<strong>Sense</strong></span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="nav__links">

          {/* Recipes Dropdown */}
          <div
            className={`nav__item ${activeDropdown === 'recipes' ? 'nav__item--active' : ''}`}
            onMouseEnter={() => openDropdown('recipes')}
            onMouseLeave={closeDropdown}
          >
            <button className="nav__link">
              Recipes
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            {activeDropdown === 'recipes' && (
              <div className="nav__dropdown">
                {nav.recipes.map(item => (
                  <Link
                    key={item.id}
                    to={`/search?category=${item.search_value}`}
                    className="nav__dropdown-item"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Ingredients Dropdown */}
          <div
            className={`nav__item ${activeDropdown === 'ingredients' ? 'nav__item--active' : ''}`}
            onMouseEnter={() => openDropdown('ingredients')}
            onMouseLeave={closeDropdown}
          >
            <button className="nav__link">
              Ingredients
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            {activeDropdown === 'ingredients' && (
              <div className="nav__dropdown">
                {nav.ingredients.map(item => (
                  <Link
                    key={item.id}
                    to={`/search?q=${item.search_value}`}
                    className="nav__dropdown-item"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {user?.role === 'admin' && (
            <Link to="/admin" className="nav__link">Admin</Link>
          )}
          <Link to="/tags" className="nav__link">Tags</Link>
        </div>

        {/* Right Side */}
        <div className="nav__right">
          {/* Search Toggle */}
          <button
            className="nav__icon-btn"
            onClick={() => setSearchOpen(!searchOpen)}
            aria-label="Search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </button>

          {/* User Avatar */}
          <div
            className={`nav__item ${activeDropdown === 'user' ? 'nav__item--active' : ''}`}
            onMouseEnter={() => openDropdown('user')}
            onMouseLeave={closeDropdown}
          >
            <button className="nav__avatar">{initials}</button>
            {activeDropdown === 'user' && (
              <div className="nav__dropdown nav__dropdown--right">
                <div className="nav__dropdown-header">
                  <strong>{user?.name}</strong>
                  <span>{user?.phone}</span>
                </div>
                <Link to="/account" className="nav__dropdown-item">
                  ⚙️ Account Settings
                </Link>
                <Link to="/bulk-import" className="nav__dropdown-item">
                  📋 Bulk Import
                </Link>
                <button className="nav__dropdown-item nav__dropdown-item--danger" onClick={handleLogout}>
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="nav__mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <span className={`nav__hamburger ${mobileOpen ? 'nav__hamburger--open' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {searchOpen && (
        <div className="nav__search">
          <form onSubmit={handleSearch} className="nav__search-form container">
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search recipes, ingredients, methods..."
              className="nav__search-input"
            />
            <button type="submit" className="btn btn-primary">Search</button>
            <button type="button" className="btn btn-ghost" onClick={() => setSearchOpen(false)}>✕</button>
          </form>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="nav__mobile">
          <div className="nav__mobile-section">
            <div className="nav__mobile-label">Recipes</div>
            {nav.recipes.map(item => (
              <Link key={item.id} to={`/search?category=${item.search_value}`} className="nav__mobile-link">
                {item.label}
              </Link>
            ))}
          </div>
          <div className="nav__mobile-section">
            <div className="nav__mobile-label">Ingredients</div>
            {nav.ingredients.map(item => (
              <Link key={item.id} to={`/search?q=${item.search_value}`} className="nav__mobile-link">
                {item.label}
              </Link>
            ))}
          </div>
          <div className="nav__mobile-section">
            <Link to="/account" className="nav__mobile-link">⚙️ Account Settings</Link>
            <Link to="/bulk-import" className="nav__mobile-link">📋 Bulk Import</Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="nav__mobile-link">Admin</Link>
            )}
            <button className="nav__mobile-link nav__mobile-link--danger" onClick={handleLogout}>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
